import React from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import { Button } from '../ui';
import { groupRef, labelFor, measureAlias, measureLabelFor } from './lib/catalog';
import { ACTIONS } from './lib/definition';
import FilterRow from './FilterRow';
import DateRangeControl from './DateRangeControl';

const SELECT = 'rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm';
const SMALL_INPUT = 'rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm py-1';
const ICON_BTN = 'p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30';

/** A card wrapping one builder section with a count badge and error state. */
export function Section({ title, count, errors = [], highlighted = false, hint, children, actions }) {
  const hasErrors = errors.length > 0;
  return (
    <section
      className={`bg-white rounded-lg border shadow-sm ${hasErrors || highlighted ? 'border-red-300 border-l-4' : 'border-gray-200'}`}
      data-section={title}
    >
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          {title}
          {count !== undefined && <span className="text-xs font-normal text-gray-500">({count})</span>}
          {hasErrors && (
            <span className="inline-flex items-center gap-1 text-xs font-normal text-red-600">
              <AlertTriangle className="w-3.5 h-3.5" /> {errors.length}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">{actions}</div>
      </header>
      <div className="p-4 space-y-2">
        {hint && <p className="text-xs text-gray-500">{hint}</p>}
        {children}
        {errors.map((e, i) => (
          <p key={i} className="text-sm text-red-600">
            {e}
          </p>
        ))}
      </div>
    </section>
  );
}

/** A selected field shown as a chip with breadcrumb, reorder arrows and remove. */
export function FieldChip({ label, breadcrumb, missing, onUp, onDown, onRemove, children, onRemoveMissing }) {
  return (
    <div className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${missing ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-900 truncate" title={breadcrumb}>
          {label}
        </div>
        {breadcrumb && breadcrumb !== label && <div className="text-[11px] text-gray-500 truncate">{breadcrumb}</div>}
        {missing && (
          <div className="text-[11px] text-amber-700">
            Missing from the dataset.{' '}
            <button type="button" className="underline" onClick={onRemoveMissing}>
              Remove everywhere
            </button>
          </div>
        )}
      </div>
      {children}
      {onUp && (
        <button type="button" className={ICON_BTN} onClick={onUp} aria-label="Move up">
          <ChevronUp className="w-4 h-4" />
        </button>
      )}
      {onDown && (
        <button type="button" className={ICON_BTN} onClick={onDown} aria-label="Move down">
          <ChevronDown className="w-4 h-4" />
        </button>
      )}
      <button type="button" className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" onClick={onRemove} aria-label="Remove">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function ColumnsSection({ definition, index, dispatch, errors, highlighted }) {
  const grouping = definition.group_by.length > 0;
  return (
    <Section
      title="Columns"
      count={definition.columns.length}
      errors={errors}
      highlighted={highlighted}
      hint={grouping ? 'While grouping, the columns are the group-by fields. Add measures for the numbers.' : 'Pick fields from the tree on the left. Rollups (Σ) appear as numbers per row.'}
    >
      {definition.columns.length === 0 && <p className="text-sm text-gray-400">No columns yet.</p>}
      {definition.columns.map((c, i) => {
        const { label, breadcrumb, missing } = labelFor(index, c);
        return (
          <FieldChip
            key={c}
            label={label}
            breadcrumb={breadcrumb}
            missing={missing}
            onRemoveMissing={() => dispatch({ type: ACTIONS.REMOVE_MISSING, id: c })}
            onUp={!grouping && i > 0 ? () => dispatch({ type: ACTIONS.MOVE_COLUMN, from: i, to: i - 1 }) : undefined}
            onDown={!grouping && i < definition.columns.length - 1 ? () => dispatch({ type: ACTIONS.MOVE_COLUMN, from: i, to: i + 1 }) : undefined}
            onRemove={() => (grouping ? dispatch({ type: ACTIONS.REMOVE_GROUP_BY, index: i }) : dispatch({ type: ACTIONS.REMOVE_COLUMN, id: c }))}
          />
        );
      })}
    </Section>
  );
}

export function GroupBySection({ definition, index, dispatch, errors, highlighted, buckets }) {
  return (
    <Section
      title="Group by"
      count={definition.group_by.length}
      errors={errors}
      highlighted={highlighted}
      hint="Use the layers icon in the tree to group. Date fields can be bucketed by day, week, month or year."
    >
      {definition.group_by.length === 0 && <p className="text-sm text-gray-400">Not grouped: one row per record.</p>}
      {definition.group_by.map((g, i) => {
        const { label, breadcrumb, missing, entry } = labelFor(index, g.field);
        const isDate = entry && (entry.type === 'date' || entry.type === 'datetime');
        return (
          <FieldChip
            key={groupRef(g)}
            label={label}
            breadcrumb={breadcrumb}
            missing={missing}
            onRemoveMissing={() => dispatch({ type: ACTIONS.REMOVE_MISSING, id: g.field })}
            onUp={i > 0 ? () => dispatch({ type: ACTIONS.MOVE_GROUP_BY, from: i, to: i - 1 }) : undefined}
            onDown={i < definition.group_by.length - 1 ? () => dispatch({ type: ACTIONS.MOVE_GROUP_BY, from: i, to: i + 1 }) : undefined}
            onRemove={() => dispatch({ type: ACTIONS.REMOVE_GROUP_BY, index: i })}
          >
            {isDate && (
              <select className={SMALL_INPUT} value={g.bucket || ''} onChange={(e) => dispatch({ type: ACTIONS.SET_GROUP_BUCKET, index: i, bucket: e.target.value || null })} aria-label="Bucket">
                <option value="">exact</option>
                {(buckets || []).map((b) => (
                  <option key={b} value={b}>
                    by {b}
                  </option>
                ))}
              </select>
            )}
          </FieldChip>
        );
      })}
    </Section>
  );
}

export function MeasuresSection({ definition, index, dispatch, errors, highlighted, catalog }) {
  const measures = [...(index.measureIndex?.values() || [])];
  const used = new Set(definition.measures.filter((m) => m.id).map((m) => m.id));
  const adhocFields = [...index.fieldIndex.values()].filter((f) => !f.filterOnly && (catalog?.adhoc_aggs_by_type?.[f.type] || []).length);
  const [adhocField, setAdhocField] = React.useState('');
  const [adhocAgg, setAdhocAgg] = React.useState('sum');
  const adhocEntry = adhocField ? index.fieldIndex.get(adhocField) : null;
  const adhocAggs = adhocEntry ? catalog?.adhoc_aggs_by_type?.[adhocEntry.type] || [] : [];

  return (
    <Section
      title="Measures"
      count={definition.measures.length}
      errors={errors}
      highlighted={highlighted}
      hint={definition.group_by.length ? 'Numbers per group.' : 'Without grouping, measures appear in the totals row only.'}
      actions={
        <select
          className={SMALL_INPUT}
          value=""
          onChange={(e) => {
            if (e.target.value) dispatch({ type: ACTIONS.ADD_MEASURE, id: e.target.value });
          }}
          aria-label="Add measure"
        >
          <option value="">+ Add measure…</option>
          {measures
            .filter((m) => !used.has(m.id))
            .map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
        </select>
      }
    >
      {definition.measures.length === 0 && <p className="text-sm text-gray-400">No measures yet.</p>}
      {definition.measures.map((m, i) => {
        const { label, missing } = measureLabelFor(index, m);
        return (
          <FieldChip
            key={`${measureAlias(m)}-${i}`}
            label={label}
            breadcrumb={m.id ? index.measureIndex.get(m.id)?.description || '' : `${m.agg} of ${m.field}`}
            missing={missing}
            onRemoveMissing={() => dispatch({ type: ACTIONS.REMOVE_MISSING, id: m.id || m.field })}
            onRemove={() => dispatch({ type: ACTIONS.REMOVE_MEASURE, index: i })}
          >
            <input
              className={`${SMALL_INPUT} w-32`}
              placeholder={measureAlias({ ...m, alias: null })}
              value={m.alias || ''}
              onChange={(e) => dispatch({ type: ACTIONS.SET_MEASURE_ALIAS, index: i, alias: e.target.value })}
              aria-label="Alias"
              title="Column name in the result"
            />
          </FieldChip>
        );
      })}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-500">Ad hoc:</span>
        <select className={SMALL_INPUT} value={adhocField} onChange={(e) => { setAdhocField(e.target.value); setAdhocAgg('sum'); }} aria-label="Ad hoc field">
          <option value="">Field…</option>
          {adhocFields.map((f) => (
            <option key={f.id} value={f.id}>
              {f.breadcrumb}
            </option>
          ))}
        </select>
        <select className={SMALL_INPUT} value={adhocAgg} disabled={!adhocEntry} onChange={(e) => setAdhocAgg(e.target.value)} aria-label="Aggregate">
          {(adhocAggs.length ? adhocAggs : ['sum']).map((a) => (
            <option key={a} value={a}>
              {a.replace('_', ' ')}
            </option>
          ))}
        </select>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={!adhocEntry || !adhocAggs.includes(adhocAgg)}
          onClick={() => {
            dispatch({ type: ACTIONS.ADD_ADHOC_MEASURE, field: adhocField, agg: adhocAgg });
            setAdhocField('');
          }}
        >
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
    </Section>
  );
}

export function FiltersSection({ definition, index, dispatch, errors, highlighted, opsByType, rowErrors }) {
  return (
    <Section
      title="Filters"
      count={definition.filters.length}
      errors={errors}
      highlighted={highlighted}
      actions={
        <Button type="button" size="sm" variant="secondary" onClick={() => dispatch({ type: ACTIONS.ADD_FILTER, opsByType })}>
          <Plus className="w-4 h-4 mr-1" /> Add filter
        </Button>
      }
    >
      {definition.filters.length === 0 && <p className="text-sm text-gray-400">No filters. All records in the date range are included.</p>}
      {definition.filters.map((f, i) => (
        <FilterRow
          key={i}
          filter={f}
          index={i}
          catalogIndex={index}
          opsByType={opsByType}
          errors={rowErrors?.[i] || []}
          onChange={(patch, fieldType) => dispatch({ type: ACTIONS.UPDATE_FILTER, index: i, patch, fieldType, opsByType })}
          onRemove={() => dispatch({ type: ACTIONS.REMOVE_FILTER, index: i })}
        />
      ))}
    </Section>
  );
}

export function SortSection({ definition, index, dispatch, errors, highlighted }) {
  const grouping = definition.group_by.length > 0;
  const options = grouping
    ? definition.group_by.map((g) => ({ id: groupRef(g), label: labelFor(index, g.field).label + (g.bucket ? ` (${g.bucket})` : '') }))
    : definition.columns.map((c) => ({ id: c, label: labelFor(index, c).breadcrumb }));
  definition.measures.forEach((m) => options.push({ id: measureAlias(m), label: `${measureLabelFor(index, m).label} (measure)` }));
  const available = options.filter((o) => !definition.sort.some((s) => s.by === o.id));
  return (
    <Section
      title="Sort"
      count={definition.sort.length}
      errors={errors}
      highlighted={highlighted}
      actions={
        <Button type="button" size="sm" variant="secondary" disabled={!available.length} onClick={() => dispatch({ type: ACTIONS.ADD_SORT, by: available[0]?.id })}>
          <Plus className="w-4 h-4 mr-1" /> Add sort
        </Button>
      }
    >
      {definition.sort.length === 0 && <p className="text-sm text-gray-400">Default order. Click a column header in the preview to sort.</p>}
      {definition.sort.map((s, i) => (
        <div key={`${s.by}-${i}`} className="flex items-center gap-2">
          <select className={`${SELECT} flex-1`} value={s.by} onChange={(e) => dispatch({ type: ACTIONS.UPDATE_SORT, index: i, patch: { by: e.target.value } })}>
            {!options.some((o) => o.id === s.by) && <option value={s.by}>⚠ {s.by}</option>}
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => dispatch({ type: ACTIONS.UPDATE_SORT, index: i, patch: { dir: s.dir === 'asc' ? 'desc' : 'asc' } })}
          >
            {s.dir === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
            {s.dir === 'asc' ? 'Ascending' : 'Descending'}
          </button>
          <button type="button" className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" onClick={() => dispatch({ type: ACTIONS.REMOVE_SORT, index: i })} aria-label="Remove sort">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </Section>
  );
}

export function DateAndOptionsSection({ definition, index, dispatch, errors, highlighted, presets, datasetNode, applied }) {
  return (
    <Section title="Date range & options" errors={errors} highlighted={highlighted}>
      <DateRangeControl
        value={definition.date_range}
        onChange={(patch) => dispatch({ type: ACTIONS.SET_DATE_RANGE, patch })}
        presets={presets}
        dateFields={index.dateFields}
        showFieldSelect={index.dateFields.length > 1}
        defaultFieldLabel={datasetNode?.date_scope === 'rollups' ? 'activity: sales, returns, purchases' : null}
        applied={applied}
      />
      {datasetNode?.date_scope === 'rollups' && (
        <p className="text-xs text-gray-500">This dataset lists master records; the date range applies to their rollups (sales, returns, purchases…).</p>
      )}
      {(datasetNode?.options || []).length > 0 && (
        <div className="flex flex-wrap gap-4 pt-2 border-t border-gray-100">
          {datasetNode.options.map((o) => (
            <label key={o.id} className="inline-flex items-center gap-2 text-sm text-gray-700" title={o.description || ''}>
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={!!definition.options?.[o.id]}
                onChange={(e) => dispatch({ type: ACTIONS.SET_OPTION, id: o.id, value: e.target.checked })}
              />
              {o.label}
            </label>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
        <label className="text-sm text-gray-700">Row limit</label>
        <input
          type="number"
          min={1}
          max={50000}
          className={`${SMALL_INPUT} w-28`}
          placeholder="none"
          value={definition.limit ?? ''}
          onChange={(e) => dispatch({ type: ACTIONS.SET_LIMIT, limit: e.target.value })}
        />
        <span className="text-xs text-gray-500">optional, max 50,000 (also caps exports)</span>
      </div>
    </Section>
  );
}
